const HERO_IMAGE =
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1600&q=80';

/**
 * Featured guide: beginner athletic training for 7th graders — parent after-school plan,
 * breathing exercises, diet basics, and 4-week stamina build with free YouTube videos.
 */
export const SEVENTH_GRADE_BEGINNER_TRAINING_POST = {
  id: 'featured-seventh-grade-beginner-training',
  slug: 'seventh-grade-beginner-training-parent-guide-stamina-breathing-diet',
  title:
    'Beginner Training for 7th Graders: After-School Plan, Breathing, Diet & Stamina',
  excerpt:
    'A parent-friendly guide for 12–13-year-olds: 45-minute after-school sessions, box breathing for game nerves, a simple fuel plan, and a 4-week stamina build with free YouTube videos. Not medical advice.',
  publishedAt: '2026-09-21T00:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Youth Training Desk',
  blogCategory: 'exercise',
  audience: 'Ages 12–13 · 7th Grade · Parents',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More youth training guides',
  relatedPosts: [
    {
      slug: 'columbus-youth-soccer-planning-kids-3-13-professional-pathway',
      title: 'Youth Soccer Planning for Kids Ages 3–13',
      blurb: 'club options and age-by-age development goals.',
    },
    {
      slug: 'columbus-youth-soccer-planning-ages-13-plus-college-pro-pathway',
      title: 'Youth Soccer Planning Ages 13+',
      blurb: 'what changes when your athlete enters middle-school club play.',
    },
    {
      slug: 'best-running-shoes-13-year-old-girl-fleet-feet-road-runner-crc',
      title: 'Best Running Shoes for 13-Year-Olds',
      blurb: 'fit and support for cross-country, PE, and after-school runs.',
    },
    {
      slug: 'soccer-team-game-day-checklist',
      title: 'Soccer Game Day Checklist',
      blurb: 'pack snacks, water, and gear for match days.',
    },
  ],
  referenceLinks: [
    {
      label: 'U.S. Soccer — Player Development Framework',
      url: 'https://www.ussoccer.com/coach/development-framework',
    },
    {
      label: 'CDC — Healthy eating for children and teens',
      url: 'https://www.cdc.gov/nutrition/features/healthy-eating-for-children-and-teens.html',
    },
    {
      label: 'HealthyChildren.org — sports nutrition for young athletes',
      url: 'https://www.healthychildren.org/English/healthy-living/sports/Pages/Sports-Nutrition-for-Young-Athletes.aspx',
    },
    {
      label: 'Aspetar — youth soccer endurance training (research summary)',
      url: 'https://www.aspetar.com/en/research-and-education/journal/view/volume-7-issue-1/youth-soccer-endurance-training',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: 'Ages 12–13', label: '7th grade window' },
        { value: '45 min', label: 'After-school session' },
        { value: '4 weeks', label: 'Stamina build' },
        { value: '10+', label: 'Free video links' },
      ],
    },
    {
      type: 'panel',
      title: 'How to use this guide',
      paragraphs: [
        'Seventh grade is when many kids join school teams, travel clubs, or cross-country—and when homework, screens, and fatigue compete with training time. This guide gives **parents a realistic after-school plan** you can run in a driveway, backyard, or park without a coaching license.',
        'Sessions stay **45 minutes or less**, mix ball work with short fitness bursts, and build stamina through small-sided games—not long punishment runs. Pair training with **simple breathing drills** for pre-game nerves and a **fuel plan** that supports growth, school, and sport.',
        'This is general fitness education, not medical advice. Talk to your child’s doctor before starting a new program—especially if they have asthma, heart conditions, prior injuries, or have been mostly inactive.',
      ],
      chips: [
        '7th grade',
        'Parents',
        'After school',
        'Stamina',
        'Breathing',
        'Nutrition',
      ],
    },
    {
      type: 'panel',
      title: 'What beginner training looks like at age 12–13',
      list: [
        '**Total volume:** 3–4 active days per week plus 1–2 rest days. More is not better at this age—recovery drives improvement.',
        '**Session length:** 40–50 minutes after school; quality drops sharply after an hour when kids are tired from class.',
        '**Intensity:** Most work at “can still talk in short sentences” effort (RPE 5–6). Save all-out sprints for short reps only.',
        '**Skill + fitness together:** Research on youth soccer shows small-sided games (4v4, 5v5) build aerobic fitness **as well as** laps—and keep kids engaged.',
        '**Growth spurts:** Soreness, clumsiness, and fatigue spike during growth spurts. Scale back volume for a week rather than pushing through.',
        '**Sleep:** 9–10 hours is the real performance booster. No training plan beats consistent sleep at this age.',
      ],
      callout:
        'If your child plays on a school or club team, treat this plan as **extra touches and conditioning**—not a replacement for coached team sessions.',
    },
    {
      type: 'panel',
      title: 'How parents can help after school',
      paragraphs: [
        'You do not need to be a former athlete. Your job is to **create structure, safety, and encouragement**—then get out of the way enough for your child to problem-solve.',
      ],
      list: [
        '**Set a fixed window:** Same 45 minutes most days (e.g., 4:30–5:15 p.m.) so homework and training do not collide.',
        '**Fuel first:** Offer water + a small snack 30–60 minutes before (see diet section). Training on an empty stomach after school leads to headaches and poor focus.',
        '**Lay out gear:** Ball, cones (or water bottles as markers), running shoes, and a water bottle ready by the door.',
        '**Warm up together for 5 minutes:** Walk/jog, arm circles, leg swings—model that prep matters.',
        '**Use a timer, not a whistle:** Phone timer for work/rest intervals keeps sessions honest without you playing drill sergeant.',
        '**Count reps, not mistakes:** Praise effort and improvement; seventh graders shut down fast under constant correction.',
        '**Film one drill occasionally:** Let them watch their form—most kids fix more from video than from yelling.',
        '**End with a cooldown + snack:** 5-minute walk and stretch, then dinner within 90 minutes.',
        '**Track simply:** A wall calendar with check marks beats spreadsheets. Note energy level (1–5) once a week.',
        '**Know when to stop:** Dizziness, sharp joint pain, or unusual chest tightness means stop and call a doctor—not “push through.”',
      ],
    },
    {
      type: 'dayBlocks',
      title: 'Sample after-school week (45 minutes each)',
      intro:
        'Adjust days to fit school practice. If your child has team training Tue/Thu, run this plan Mon/Wed/Sat only. Each block lists the session flow—**meals** here means timed segments, not food.',
      days: [
        {
          title: 'Monday — Ball skills + short shuttle runs',
          meals: [
            '0–8 min: Dynamic warm-up (jog, high knees, butt kicks, side shuffle).',
            '8–25 min: Dribbling circuit—4 cones in a square, 1 min each: toe taps, inside-outside, sole rolls, figure-8. Rest 30 sec between.',
            '25–38 min: Shuttle runs—5 × 15-yard sprint there-and-back, 45 sec rest. Add a ball on 3 reps if comfortable.',
            '38–45 min: Cooldown walk + box breathing (see breathing section).',
          ],
        },
        {
          title: 'Tuesday — Small-sided game day (stamina focus)',
          meals: [
            '0–8 min: Warm-up jog + dynamic stretches.',
            '8–35 min: 4v4 or 3v3 on a small field (30 × 20 yds). Play to 3 goals, then switch teams. No coaching every touch—let them play.',
            'Optional variation: “Bank game”—teams earn points for consecutive passes before scoring (see video below).',
            '35–45 min: Light jog + stretch calves, quads, hip flexors.',
          ],
        },
        {
          title: 'Wednesday — Active recovery + breathing',
          meals: [
            '0–10 min: Easy 10-minute walk or bike with your child—conversation pace.',
            '10–20 min: Mobility—hip circles, ankle rolls, gentle hamstring stretch (no bouncing).',
            '20–30 min: Breathing practice—box breathing and belly breathing (see section below).',
            '30–45 min: Optional 10 minutes of free juggling or shooting for fun—no structure.',
          ],
        },
        {
          title: 'Thursday — Agility + core (no ball optional)',
          meals: [
            '0–8 min: Warm-up.',
            '8–22 min: Ladder or cone footwork—icky shuffle, two-feet-in-each, lateral steps. 3 sets of 4 reps, easy jog back.',
            '22–35 min: Core circuit—30 sec each: plank, dead bug, glute bridge, side plank. 2 rounds, 30 sec rest.',
            '35–45 min: Cooldown + hydration check (half their body weight in ounces of water daily as a rough target).',
          ],
        },
        {
          title: 'Friday — Rest or cross-training',
          meals: [
            'Full rest, or 30–45 min of swimming, biking, hiking, or shooting hoops—low structure, high fun.',
            'No timed sprints. Sleep and school come first on Fridays.',
          ],
        },
        {
          title: 'Saturday — Stamina builder (week progression)',
          meals: [
            'Follow the 4-week stamina table below—this is the longest session of the week (45–50 min).',
            'Always include a ball for at least half the session.',
            'Celebrate finishing—not pace. Compare to last week, not to teammates.',
          ],
        },
      ],
    },
    {
      type: 'panel',
      title: 'Breathing exercises for young athletes',
      paragraphs: [
        'Seventh graders often arrive at games with shallow chest breathing, tight shoulders, and pre-game nerves. **Breath work is free, portable, and proven to lower heart rate and improve focus**—but only if practiced on calm days, not first introduced 5 minutes before kickoff.',
      ],
      list: [
        '**Box breathing (4-4-4-4):** Inhale through the nose for 4 counts → hold 4 → exhale through the mouth for 4 → hold 4. Repeat 4–6 cycles. Use before games, at halftime, or when anxiety spikes before a test.',
        '**Belly (diaphragmatic) breathing:** Hand on stomach. Inhale so the belly rises (not the shoulders). Exhale slowly. 5 breaths. Builds lung awareness for running.',
        '**Recovery breathing after sprints:** Walk slowly, hands on hips, exhale fully through pursed lips for 6–8 counts. Do not bend over with hands on knees—that restricts the diaphragm.',
        '**Pre-sleep wind-down:** 2 minutes of box breathing in bed helps sleep quality, which drives stamina more than an extra drill.',
      ],
      callout:
        'Practice breathing **3× per week on non-game days** for two weeks before expecting it to work under pressure.',
    },
    {
      type: 'videoEmbed',
      title: 'Watch: Box breathing for kids',
      intro:
        'This 90-second guide from **Lurie Children’s Hospital** walks through square (box) breathing—a technique pediatric specialists use with anxious or stressed kids. Practice together after school on Wednesday.',
      embeds: [
        {
          youtubeId: 'ummIQOQFHZA',
          title: 'Square Breathing (Box Breathing) — Coping Skills for Kids',
          caption:
            'Trace a square in the air: breathe in, hold, breathe out, hold—four counts each side.',
        },
      ],
    },
    {
      type: 'videoList',
      title: 'More breathing & calm-down videos',
      videos: [
        {
          title: 'Box Breathing Exercise For Kids (1 Min)',
          channel: 'Learn With Ms. Prima',
          description:
            'Short follow-along box breathing—good for winding down after school before homework.',
          url: 'https://www.youtube.com/watch?v=dv77qUpuO2A',
        },
        {
          title: 'Using Box Breathing To Take Your Mental Game To The Next Level',
          channel: 'Total Female Hockey',
          description:
            'Athlete-focused 4×4 box breathing with optional arm movement—applies to any sport, not just hockey.',
          url: 'https://www.youtube.com/watch?v=unPu8bMFKZ8',
        },
      ],
    },
    {
      type: 'table',
      title: 'Simple diet plan for active 7th graders',
      intro:
        'Growing athletes need regular meals—not fad diets or adult portion sizes. These are **templates**, not prescriptions. Adjust for allergies, culture, and appetite. A registered dietitian can personalize for your child.',
      headers: ['Meal', 'School day (no evening practice)', 'Practice / game day'],
      rows: [
        [
          'Breakfast',
          'Protein + complex carb: eggs or yogurt + whole-grain toast or oatmeal; fruit on the side. Water or milk.',
          'Same, eaten 2–3 hours before activity. Avoid brand-new foods on game day.',
        ],
        [
          'Lunch',
          'Balanced tray: lean protein (chicken, beans, turkey), rice or whole-grain bread, vegetables, fruit. Pack water—not energy drinks.',
          'Add an extra piece of fruit or whole-grain snack if lunch was early (before noon).',
        ],
        [
          'After-school snack (60 min pre-training)',
          'Optional if dinner is soon: apple + peanut butter, banana, or granola bar + water.',
          '**Required:** carb + a little protein—banana + string cheese, trail mix, or half a turkey sandwich. 12–16 oz water.',
        ],
        [
          'During practice (60+ min)',
          '—',
          'Water every 15–20 min. Sports drink only if session exceeds 90 min in heat and they sweat heavily.',
        ],
        [
          'Dinner (within 90 min after training)',
          'Protein + starch + vegetables: grilled chicken, rice, broccoli; or lentil curry + rice; or tacos with lean meat and beans.',
          'Same structure—**do not skip dinner to “stay light.”** Growth and recovery need fuel.',
        ],
        [
          'Hydration',
          'Rough guide: half body weight (lbs) in ounces of water daily. Pale yellow urine = on track.',
          'Add 16–24 oz water for every hour of hard practice. Weigh before/after once to learn sweat rate.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Snacks and foods to limit',
      list: [
        '**Skip before training:** greasy fast food, large milkshakes, candy-only snacks, and energy drinks (high caffeine is not safe for 12–13-year-olds).',
        '**Fine in moderation:** chips, soda, and dessert—just not in the hour before practice.',
        '**Iron and calcium matter:** lean meats, beans, leafy greens, dairy or fortified alternatives support bone growth and energy.',
        '**Breakfast skippers:** If your child skips breakfast, a small snack (banana + yogurt) before school beats training on fumes.',
      ],
    },
    {
      type: 'table',
      title: '4-week stamina improvement plan',
      intro:
        'One **Saturday stamina session** per week, plus the Monday/Tuesday work above. Progress only when your child completes the week feeling “tired but good”—not exhausted for two days.',
      headers: ['Week', 'Saturday session', 'Target', 'Parent checkpoint'],
      rows: [
        [
          '1 — Base',
          'Warm-up 8 min → 4v4 for 12 min (small field) → 6 × 20-yard shuttle sprint, 60 sec rest → cooldown',
          'Finish all reps with good form',
          'Can they talk in full sentences within 2 min of finishing?',
        ],
        [
          '2 — Build',
          'Warm-up → 3 × 8 min 5v5 (2 min rest between) → 8 × 15-yard sprint with ball, 45 sec rest',
          'Stay engaged through last 5v5 block',
          'Note energy 1–5 that evening. Should be 3–4, not 1.',
        ],
        [
          '3 — Push',
          'Warm-up → “Bank game” 15 min → 4 × 4 min jog/sprint intervals (30 sec fast / 90 sec easy jog) → cooldown',
          'Complete intervals without walking',
          'Any sharp pain? Stop and rest 3–5 days.',
        ],
        [
          '4 — Test & celebrate',
          'Warm-up → 4v4 for 20 min continuous → time a 1-mile easy jog or 6-min run (optional) → breathing cooldown',
          'Compare mile/time to week 1 if tracked',
          'Reward consistency, not speed—pizza night, new socks, or pick next week’s drill.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'At-home stamina drills (no field required)',
      list: [
        '**Stair repeats:** Walk up, jog down, 6–10 times. Stop if knees hurt.',
        '**Jump rope:** 30 sec on / 30 sec off × 8. Great foot speed and cardio.',
        '**Shadow soccer:** 1 min each—high knees, side shuffles, backpedal, sprint—all with or without a ball.',
        '**Wall passes:** 100 touches against a wall or rebounder—low intensity, builds touch under fatigue.',
        '**Plank-to-sprint:** 20 sec plank → 20-yard sprint × 6. Full rest between.',
      ],
    },
    {
      type: 'videoList',
      title: 'Free YouTube training videos for 7th graders',
      intro:
        'Watch once with your child, then pick **one or two drills** to add each week. Avoid trying every drill in one session.',
      videos: [
        {
          title: 'TOP 10 STAMINA FOR SOCCER DRILLS',
          channel: 'Progressive Soccer',
          description:
            'Ten conditioning drills you can scale—sprints, agility, and ball work. Start with one drill and add volume slowly.',
          url: 'https://www.youtube.com/watch?v=LIKEIgl1n44',
        },
        {
          title: 'Drill of the Day — The Bank Game',
          channel: 'Youth soccer coaching',
          description:
            'Passing game that builds fitness through small-sided play—perfect for Week 3 of the stamina plan.',
          url: 'https://www.youtube.com/watch?v=WXfgzMBn0Mc',
        },
        {
          title: 'U10 Soccer Training — 3 Team Rotation (movement & stamina)',
          channel: 'Grassroots coaching',
          description:
            'Three-team rotation keeps everyone moving with short rest—ideal format for 12–13-year-olds.',
          url: 'https://www.youtube.com/watch?v=8H5o_kCJx-U',
        },
        {
          title: 'How to Do a Dynamic Team Warm Up',
          channel: 'Online Soccer Academy (Emily Grant)',
          description:
            'Jog, backpedal, high knees, butt kicks, side shuffle, lunges—use the first 8 minutes of every session.',
          url: 'https://www.youtube.com/watch?v=mkISUseWPkU',
        },
        {
          title: 'IMPROVE Your Youth Soccer Training Sessions — U9/U10 Ideas',
          channel: 'Youth coaching',
          description:
            'How to structure activation games and small-sided play—applies directly to parent-led after-school sessions.',
          url: 'https://www.youtube.com/watch?v=BSobYW-sDHE',
        },
      ],
    },
    {
      type: 'panel',
      title: 'Recovery rules for the whole month',
      list: [
        '**One full rest day per week minimum**—no structured training. Walks and play are fine.',
        '**Sleep 9–10 hours** when possible. Phones off 30 minutes before bed.',
        '**Stretch calves and hip flexors** 5 minutes after every hard session—growth-spurt tightness is common at 12–13.',
        '**Ice or rest** for pain that changes how they walk. See a clinician if pain lasts more than 5–7 days.',
        '**Deload week:** Every 4th week, cut volume by 30% even if they feel great—prevents overuse injuries.',
      ],
      callout:
        'Consistency over 4 weeks beats one heroic week. Missing a day is normal; missing three in a row means reset the plan, do not double up.',
    },
  ],
};
